import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { actions } from '../../store/conditionQueryTemplate';
import { produce } from 'immer';
import FormController from '../../common/formController';
import Joi from 'joi';
import validators from './validator';
import { ProductType } from 'constants.js';
import { getSchemas } from 'store/schema/actions';
import { shouldPreserveCopyTemplateState } from 'helpers/templateCopyGuard';

const {
  getConditionQueryTemplate,
  setCurrentConditionQueryTemplate,
  updateConditionQueryTemplate,
  createConditionQueryTemplate,
  setNextStep,
  unsetNextStep,
  setCopyCurrentConditionQueryTemplate
} = actions;

let formController = new FormController();

export default function useTemplate(nextPath, backPath) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { queryId: originalQueryId } = useParams();
  const template = useSelector(store => store.conditionQueryTemplate.current);
  const isLoading = useSelector(store => store.conditionQueryTemplate.getting);
  const isTemplateSaving = useSelector(
    store => store.conditionQueryTemplate.isTemplateSaving
  );
  const isTemplateSaved = useSelector(
    store => store.conditionQueryTemplate.isTemplateSaved
  );
  const productIntegrationOptions = useSelector(
    store => store.events.supportedEventTypes.productIntegrationOptions
  );
  const allEventTypeCategoryOptions = useSelector(
    store => store.events.supportedEventTypes.allEventTypeCategoryOptions
  );
  const supportedOnDemandTypeOptions = useSelector(
    store => store.events.supportedOnDemandTypes
  );
  const currentBusinessContextId = useSelector(
    store => store.user.selectedBusinessContext.businessContextId
  );
  const listManagementSchema = useSelector(
    store => store.schema.listManagementSchema
  );
  const guestInsightSchema = useSelector(
    store => store.schema.guestInsightSchema
  );
  const [validationErrors, setValidationErrors] = useState({});
  const [errorValidationSegment, setErrorValidationSegment] = useState(null);
  const [queryId, setQueryId] = useState(originalQueryId);
  const [thisTemplate, setThisTemplate] = useState({
    type: '',
    name: ''
  });

  const validate = (saveAttempted = false) => {
    let segment = errorValidationSegment;
    const joiValidators = Joi.object(validators[segment]);
    const validation = joiValidators.validate(thisTemplate, {
      abortEarly: false,
      allowUnknown: true // allow unknown keys that will be ignored
    });
    let errors = formController.getFormErrors(validation, { saveAttempted });
    setValidationErrors(errors);
    return errors;
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!isLoading) {
      const queryParams = new URLSearchParams(location.search);
      const businessContextId = queryParams.get('businessContextId');
      
      dispatch(getConditionQueryTemplate({ 
        templateId: queryId || template.uuid,
        ...(businessContextId && { businessContextId })
      }));
    }
    if (Object.keys(template).length > 0) {
      formController = new FormController();
      setThisTemplate({ ...template });
    }
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const copy = queryParams.get('copy');
    
    if (copy === 'true') {
      setQueryId(undefined);
      
      let queryToCopy = Object.assign(
        {},
        template
      );
      if (queryToCopy && queryToCopy.uuid) {
        delete queryToCopy.id;
        delete queryToCopy.uuid;
        delete queryToCopy.defaultTemplate;
        queryToCopy._isCopy = true;
        queryToCopy._isCopyTemplate = true;
        queryToCopy.name = queryToCopy.name + `-copy`;
        queryToCopy.schemaIdentifier = queryToCopy.schemaIdentifier
          ? queryToCopy.schemaIdentifier
          : queryToCopy.productCategory
            ? `${queryToCopy.productCategory}.${queryToCopy.productName}.${queryToCopy.eventTypeCategory}`
            : '';
        // Update businessContextId for copied queries
        queryToCopy.businessContextId = currentBusinessContextId;
  
        setThisTemplate(prevTemplate => ({
          ...queryToCopy,
          hasMovedToNextStep: prevTemplate.hasMovedToNextStep || false
        }));
        
        const newUrl = '/condition-queries/details';
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [location.search, template.uuid, setCopyCurrentConditionQueryTemplate]);

  useEffect(() => {
    if (
      productIntegrationOptions?.filter(option => option != 'itinerary')
        .length === 1 &&
      !thisTemplate.hasOwnProperty('productName') &&
      thisTemplate.productType !== ProductType.List &&
      thisTemplate.productType !== ProductType.GUEST_INSIGHTS
    ) {
      handleAutomationChange({
        target: { name: 'productName', value: productIntegrationOptions[0] }
      });
    }
  }, [thisTemplate]);

  useEffect(() => {
    if (Object.keys(template).length > 0) {
      formController = new FormController();
      const queryParams = new URLSearchParams(location.search);
      const isCopyMode = queryParams.get('copy') === 'true';

      setThisTemplate(prevState => {
        const isUnsavedCopyFlow = shouldPreserveCopyTemplateState({
          isCopyMode,
          previousTemplate: prevState,
          incomingTemplate: template
        });

        if (isUnsavedCopyFlow) {
          return prevState;
        }

        if (!queryId) {
          return {
            ...prevState,
            ...template,
            hasMovedToNextStep: template.hasMovedToNextStep || false
          };
        }

        return { ...prevState, ...template };
      });
    }
  }, [template, location]);

  useEffect(() => {
    if (
      thisTemplate.productType &&
      thisTemplate.productType === ProductType.List
    ) {
      setThisTemplate({
        ...thisTemplate,
        productCategory: listManagementSchema?.eventProductCategory || '',
        productName: listManagementSchema?.eventProductName || '',
        eventTypeCategory: listManagementSchema?.eventTypeCategory || '',
        schemaIdentifier:
          listManagementSchema?.eventProductCategory +
            '.' +
            listManagementSchema?.eventProductName +
            '.' +
            listManagementSchema?.eventTypeCategory || '',
        partialCollectionName:
          listManagementSchema?.schemaToEventPartialCollectionName || ''
      });
    }
  }, [listManagementSchema]);

  useEffect(() => {
    if (
      thisTemplate.productType &&
      thisTemplate.productType === ProductType.GUEST_INSIGHTS
    ) {
      setThisTemplate({
        ...thisTemplate,
        productCategory: guestInsightSchema?.eventProductCategory || '',
        productName: guestInsightSchema?.eventProductName || '',
        eventTypeCategory: guestInsightSchema?.eventTypeCategory || '',
        schemaIdentifier:
          guestInsightSchema?.eventProductCategory +
            '.' +
            guestInsightSchema?.eventProductName +
            '.' +
            guestInsightSchema?.eventTypeCategory || '',
        partialCollectionName:
        guestInsightSchema?.schemaToEventPartialCollectionName || ''
      });
    }
  }, [guestInsightSchema]);

  const goNext = event => {
    const errors = validate(true);
    if (errors.meta.clean) {
      dispatch(
        setCurrentConditionQueryTemplate({
          ...thisTemplate,
          hasMovedToNextStep: true
        })
      );
      formController = new FormController();
      if (
        nextPath ||
        (event.currentTarget.attributes.link &&
          event.currentTarget.attributes.link.value)
      ) {
        history.push(
          `${nextPath || event.currentTarget.attributes.link.value}${
            (queryId && !(thisTemplate._isCopy || thisTemplate._isCopyTemplate)) ? `/${queryId}` : ''
          }`
        )
      } else {
        setThisTemplate({
          ...thisTemplate,
          hasMovedToNextStep: true
        });
        dispatch(setNextStep());
      }
    }
  };
  const resetValidationErrors = () => {
    setValidationErrors({});
    setThisTemplate({
      type: '',
      name: ''
    });
  };

  const goBack = event => {
    dispatch(setCurrentConditionQueryTemplate({ ...thisTemplate }));
    if (
      backPath ||
      (event.currentTarget.attributes.link &&
        event.currentTarget.attributes.link.value)
    ) {
      let id = queryId ? `/${queryId}` : '';
      history.push(
        `${backPath || event.currentTarget.attributes.link.value}${id}`
      );
    } else {
      dispatch(unsetNextStep());
    }
  };

  const navBackOnError = path => {
    if (
      thisTemplate.hasOwnProperty('hasMovedToNextStep') &&
      !thisTemplate.hasMovedToNextStep &&
      !thisTemplate.name
    ) {
      dispatch(
        setCurrentConditionQueryTemplate({
          ...thisTemplate,
          hasMovedToNextStep: false
        })
      );
      history.push(path);
    }
  };

  const onTabChange = () => {
    dispatch(setCurrentConditionQueryTemplate(thisTemplate));
  };

  const setByPath = (obj, path, value) => {
    if (typeof path === 'string') {
      path = path.split('.');
    }
    if (obj && path.length === 1) {
      obj[path] = value;
      return;
    }
    return setByPath(obj[path[0]], path.slice(1), value);
  };

  const handleChange = events => {
    events = Array.isArray(events) ? events : [events];

    if (events.length > 0) {
      let templateUpdate = { ...thisTemplate };
      events.forEach(event => {
        setByPath(templateUpdate, event.target.name, event.target.value);
      });
      setThisTemplate(templateUpdate);
      validate();
    }
  };

  const handleAutomationChange = event => {
    const { name, value } = event.target;
    let typeOption = {};
    let templateUpdate = produce(thisTemplate, draft => {
      typeOption = allEventTypeCategoryOptions?.find(
        option => option.id === value
      );

      draft.productCategory = typeOption?.productCategory || '';
      draft.productName = typeOption?.productName || '';
      draft.eventTypeCategory = typeOption?.eventTypeCategory || '';
      draft.schemaIdentifier = typeOption?.schemaIdentifier || '';
      draft.schemaId = typeOption?.schemaId || '';
      draft.partialCollectionName = typeOption?.partialCollectionName || '';
      setByPath(draft, name, value);
    });
    setThisTemplate(templateUpdate);
    validate(false);
  };

  const saveTemplate = () => {
    const errors = validate(true);
    if (errors.meta.clean) {
      dispatch(setCurrentConditionQueryTemplate({ ...thisTemplate }));
      if (thisTemplate.uuid) {
        dispatch(updateConditionQueryTemplate());
      } else {
        dispatch(createConditionQueryTemplate());
      }
      formController = new FormController();
    }
  };

  const onBlur = e => {
    formController.onBlur(e);
    validate();
  };

  const onFocus = e => {
    formController.onFocus(e);
    validate();
  };

  const isQueryReadOnly = () => {
    return (
      thisTemplate.uuid &&
      currentBusinessContextId !== thisTemplate.businessContextId
    );
  };

  const closeQuery = () => {
    formController = new FormController();
    history.push('/condition-queries');
  };

  const shouldShowError = name => {
    return formController.shouldShowError(validationErrors, name);
  };

  const getErrorMessage = name => {
    return formController.getErrorMessage(validationErrors, name);
  };

  const showFormSaveError = () => {
    return formController.showFormSaveError(validationErrors);
  };

  const updateCurrentErrorValidationSegment = name => {
    setErrorValidationSegment(name);
  };

  const handleTypeChange = event => {
    if (event.target.value === ProductType.List || event.target.value === ProductType.GUEST_INSIGHTS) {
      dispatch(getSchemas({ showActive: true }));
    }
    setThisTemplate({
      ...thisTemplate,
      productType: event.target.value,
      eventType: '',
      productCategory: '',
      productName: '',
      eventTypeCategory: '',
      schemaIdentifier: '',
      subscriberListId: '',
      segmentedListId: ''
    });
  };

  const handleSubscriberListChange = (event, option) => {
    if (!option) {
      setThisTemplate({
        ...thisTemplate,
        subscriberListId: '',
        segmentedListId: ''
      });
      return;
    }
    setThisTemplate({
      ...thisTemplate,
      ...(option.listId
        ? { subscriberListId: option.listId, segmentedListId: option.id }
        : { subscriberListId: option.id, segmentedListId: '' })
    });
  };

  return {
    thisTemplate,
    validationErrors,
    handleChange,
    handleAutomationChange,
    goNext,
    goBack,
    navBackOnError,
    onTabChange,
    saveTemplate,
    onFocus,
    onBlur,
    isQueryReadOnly,
    closeQuery,
    showFormSaveError,
    getErrorMessage,
    shouldShowError,
    updateCurrentErrorValidationSegment,
    supportedOnDemandTypeOptions,
    productIntegrationOptions,
    allEventTypeCategoryOptions,
    isLoading,
    isTemplateSaving,
    resetValidationErrors,
    isTemplateSaved,
    handleTypeChange,
    handleSubscriberListChange
  };
}
